// apps/web/src/features/crm/leads/list/EngagementTaskIndicator.tsx
//
// Next-step signal for one engagement row (#2307 D-2, #2304 D-1). The list
// projects a SINGLE open task's presence (`hasOpenTask`) plus its optional due
// date (`openTaskDueAt`); overdue is derived client-side.
// INVARIANT (#2304 D-1): `openTaskDueAt === null` is ambiguous — it means both
// «no due date» AND «no open task» — so `hasOpenTask` is the ONLY disambiguator.
// An open task with no due date renders the non-overdue open state, NEVER the
// no-next-step warning.
import { useTranslation } from 'react-i18next';
import { EngagementPhase, EngagementStage, isTerminalEngagementStage } from '@magimp/shared';
import { Icon } from '../../../../ui/index.js';

export interface EngagementTaskIndicatorProps {
  stage: EngagementStage;
  phase: EngagementPhase;
  hasOpenTask: boolean | undefined;
  openTaskDueAt: string | null | undefined;
}

export function EngagementTaskIndicator({
  stage,
  phase,
  hasOpenTask,
  openTaskDueAt,
}: EngagementTaskIndicatorProps) {
  const { t } = useTranslation(['leads']);

  if (hasOpenTask === true) {
    const overdue = openTaskDueAt != null && new Date(openTaskDueAt).getTime() < Date.now();
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          color: overdue ? 'var(--status-error-fg)' : 'var(--fg-2)',
        }}
      >
        <Icon name={overdue ? 'timer-off' : 'check-square'} size={13} />1
      </span>
    );
  }

  // No open task: warn only while the engagement is still actionable
  // (non-terminal LEAD stage and not yet a DEAL); otherwise a muted dash.
  const active = !isTerminalEngagementStage(stage) && phase !== EngagementPhase.DEAL;
  if (active) {
    return (
      <span
        title={t('leads:list.noNextStep')}
        style={{ display: 'inline-flex', color: 'var(--status-warn-fg)' }}
      >
        <Icon name="alert-circle" size={15} />
      </span>
    );
  }
  return <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>—</span>;
}
